<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('adminAttendance.title')" :description="t('adminAttendance.description')">
      <template #actions>
        <AppButton variant="outline" size="sm" @click="exportAttendance">
          <Download class="h-4 w-4" /> {{ t('common.export') }}
        </AppButton>
        <AppButton size="sm" :loading="loading" @click="refreshAttendance">
          <RefreshCw class="h-4 w-4" /> {{ t('common.refresh') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stats -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard :label="t('common.approved')" :value="stats.approved" :icon="UserCheck" iconBg="bg-emerald-50" iconColor="text-emerald-600" :loading="loading" />
      <AppStatCard :label="t('common.rejected')" :value="stats.rejected" :icon="UserX"     iconBg="bg-red-50"     iconColor="text-red-600"     :loading="loading" />
      <AppStatCard :label="t('common.pending')"  :value="stats.pending"  :icon="Clock"     iconBg="bg-amber-50"   iconColor="text-amber-600"   :loading="loading" />
      <AppStatCard :label="t('adminAttendance.totalRecords')" :value="stats.total" :icon="Users" iconBg="bg-blue-50" iconColor="text-blue-600" :loading="loading" />
    </div>

    <!-- Filters -->
    <AppCard class="p-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('adminAttendance.filterByDate') }}</label>
          <AppSelect v-model="dateRange">
            <option value="today">{{ t('common.today') }}</option>
            <option value="week">{{ t('common.thisWeek') }}</option>
            <option value="month">{{ t('common.thisMonth') }}</option>
          </AppSelect>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('common.department') }}</label>
          <AppSelect v-model="selectedDepartment">
            <option value="">{{ t('adminAttendance.allDepartments') }}</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
          </AppSelect>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('common.status') }}</label>
          <AppSelect v-model="selectedStatus">
            <option value="">{{ t('adminAttendance.allStatuses') }}</option>
            <option value="pending">{{ t('common.pending') }}</option>
            <option value="approved">{{ t('common.approved') }}</option>
            <option value="auto_approved">{{ t('adminAttendance.autoApproved') }}</option>
            <option value="rejected">{{ t('common.rejected') }}</option>
          </AppSelect>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('adminAttendance.filterByEmployee') }}</label>
          <AppInput v-model="searchQuery" :placeholder="t('adminAttendance.searchPlaceholder')">
            <template #prefix><Search class="h-3.5 w-3.5" /></template>
          </AppInput>
        </div>
      </div>
    </AppCard>

    <!-- Table -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border">
        <p class="text-sm font-medium text-foreground">{{ t('adminAttendance.title') }}</p>
      </div>
      <AppTable
        :columns="columns"
        :rows="attendanceRecords"
        :loading="loading"
        :empty-text="t('adminAttendance.noRequests')"
      >
        <!-- Employee cell -->
        <template #cell-employee="{ row }">
          <div class="flex items-center gap-3">
            <img
              v-if="row.employee?.profile_photo"
              :src="row.employee.profile_photo"
              :alt="row.employee.name"
              class="h-8 w-8 rounded-full object-cover shrink-0"
            />
            <div v-else class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {{ initials(row.employee?.name || row.userName) }}
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-foreground truncate">{{ row.employee?.name || row.userName }}</p>
              <p class="text-xs text-muted-foreground">{{ row.employee?.employee_code || row.employee_code }}</p>
            </div>
          </div>
        </template>

        <!-- Type cell -->
        <template #cell-type="{ row }">
          <div class="flex flex-wrap gap-1">
            <AppBadge :variant="row.type === 'clock_in' ? 'default' : 'secondary'">
              {{ row.type === 'clock_in' ? t('adminAttendance.clockIn') : t('adminAttendance.clockOut') }}
            </AppBadge>
            <AppBadge v-if="row.isAutoGenerated" variant="warning">{{ t('common.system') }}</AppBadge>
          </div>
        </template>

        <!-- Time cell -->
        <template #cell-time="{ row }">
          <span class="text-sm font-mono">{{ formatTime(row.requested_timestamp) }}</span>
        </template>

        <!-- Status cell -->
        <template #cell-status="{ row }">
          <AppBadge :variant="statusVariant(row.status)">{{ getStatusText(row.status) }}</AppBadge>
        </template>

        <!-- Actions -->
        <template #actions="{ row }">
          <div class="flex items-center gap-1 justify-end">
            <AppButton variant="ghost" size="icon-sm" :title="t('common.details')" @click="viewDetails(row)">
              <Eye class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton v-if="row.status === 'pending'" variant="ghost" size="icon-sm" class="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" :title="t('common.approve')" @click="quickApprove(row)">
              <CheckCircle class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton v-if="row.status === 'pending'" variant="ghost" size="icon-sm" class="text-destructive hover:text-destructive hover:bg-red-50" :title="t('common.reject')" @click="quickReject(row)">
              <XCircle class="h-3.5 w-3.5" />
            </AppButton>
          </div>
        </template>
      </AppTable>

      <!-- Pagination -->
      <div class="px-4 py-3 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p class="text-xs text-muted-foreground">
          {{ t('adminAttendance.showing', { from: totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1, to: Math.min(currentPage * itemsPerPage, totalRecords), total: totalRecords }) }}
        </p>
        <div class="flex items-center gap-1">
          <AppButton variant="outline" size="icon-sm" :disabled="currentPage === 1" @click="previousPage">
            <ChevronLeft class="h-4 w-4" />
          </AppButton>
          <button
            v-for="page in visiblePages"
            :key="page"
            :class="['h-7 min-w-[28px] px-2 rounded-md text-xs font-medium transition-colors', page === currentPage ? 'bg-primary text-primary-foreground' : 'hover:bg-accent text-foreground']"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <AppButton variant="outline" size="icon-sm" :disabled="currentPage === totalPages" @click="nextPage">
            <ChevronRight class="h-4 w-4" />
          </AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Details Modal -->
    <AppModal v-model="showModal" :title="t('adminAttendance.requestDetails')" size="lg">
      <div v-if="selectedRecord" class="space-y-5">

        <!-- Employee info -->
        <div class="flex items-center gap-4">
          <img v-if="selectedRecord.employee?.profile_photo" :src="selectedRecord.employee.profile_photo" class="h-14 w-14 rounded-full object-cover shrink-0" />
          <div v-else class="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
            {{ initials(selectedRecord.employee?.name || selectedRecord.userName) }}
          </div>
          <div>
            <p class="font-semibold text-foreground">{{ selectedRecord.employee?.name || selectedRecord.userName }}</p>
            <p class="text-sm text-muted-foreground">{{ selectedRecord.employee?.employee_code || selectedRecord.employee_code }} · {{ selectedRecord.department || 'N/A' }}</p>
            <p class="text-sm text-muted-foreground">{{ selectedRecord.employee?.position || selectedRecord.position || '' }}</p>
          </div>
        </div>

        <!-- Auto clock-out banner -->
        <div v-if="selectedRecord.isAutoGenerated" class="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <span class="text-amber-500 text-base leading-none shrink-0">⚙️</span>
          <div>
            <p class="text-sm font-semibold text-amber-800">{{ t('attendance.autoClockOutBanner') }}</p>
            <p class="text-xs text-amber-700 mt-0.5">{{ t('adminAttendance.autoClockOutDesc') }}</p>
          </div>
        </div>

        <!-- Details grid -->
        <div class="grid grid-cols-2 gap-3">
          <div class="rounded-lg bg-muted/50 p-3">
            <p class="text-xs text-muted-foreground mb-1">{{ t('adminAttendance.type') }}</p>
            <div class="flex flex-wrap gap-1">
              <AppBadge :variant="selectedRecord.type === 'clock_in' ? 'default' : 'secondary'">
                {{ selectedRecord.type === 'clock_in' ? t('adminAttendance.clockIn') : t('adminAttendance.clockOut') }}
              </AppBadge>
              <AppBadge v-if="selectedRecord.isAutoGenerated" variant="warning">{{ t('common.system') }}</AppBadge>
            </div>
          </div>
          <div class="rounded-lg bg-muted/50 p-3">
            <p class="text-xs text-muted-foreground mb-1">{{ t('common.status') }}</p>
            <AppBadge :variant="statusVariant(selectedRecord.status)">{{ getStatusText(selectedRecord.status) }}</AppBadge>
          </div>
          <div class="rounded-lg bg-muted/50 p-3">
            <p class="text-xs text-muted-foreground mb-1">{{ t('adminAttendance.requestedTime') }}</p>
            <p class="text-sm font-medium text-foreground font-mono">{{ formatDateTime(selectedRecord.requested_timestamp) }}</p>
          </div>
          <div v-if="selectedRecord.is_time_capped" class="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p class="text-xs text-amber-600 mb-1">{{ t('adminAttendance.payrollTimeCapped') }}</p>
            <p class="text-sm font-medium text-amber-900 font-mono">{{ formatDateTime(selectedRecord.payroll_timestamp) }}</p>
            <p class="text-xs text-amber-600 mt-0.5">{{ selectedRecord.capping_reason }}</p>
          </div>
          <div v-else class="rounded-lg bg-muted/50 p-3">
            <p class="text-xs text-muted-foreground mb-1">{{ t('adminAttendance.source') }}</p>
            <p class="text-sm font-medium text-foreground capitalize">
              {{ selectedRecord.isAutoGenerated ? t('adminAttendance.systemAutoClockOut') : (selectedRecord.source || 'mobile') }}
            </p>
          </div>
        </div>

        <!-- Location -->
        <div v-if="selectedRecord.address || selectedRecord.latitude" class="rounded-lg bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground mb-2">{{ t('adminAttendance.location') }}</p>
          <p class="text-sm text-foreground mb-2">{{ selectedRecord.address || t('adminAttendance.noAddress') }}</p>
          <div v-if="selectedRecord.latitude && selectedRecord.longitude">
            <iframe
              :src="`https://www.openstreetmap.org/export/embed.html?bbox=${Number(selectedRecord.longitude)-0.003},${Number(selectedRecord.latitude)-0.003},${Number(selectedRecord.longitude)+0.003},${Number(selectedRecord.latitude)+0.003}&layer=mapnik&marker=${selectedRecord.latitude},${selectedRecord.longitude}`"
              class="w-full h-48 rounded-md border border-border"
              loading="lazy"
              referrerpolicy="no-referrer"
            />
            <p class="text-xs text-muted-foreground mt-1">
              {{ Number(selectedRecord.latitude).toFixed(6) }}, {{ Number(selectedRecord.longitude).toFixed(6) }}
              <a :href="`https://www.openstreetmap.org/?mlat=${selectedRecord.latitude}&mlon=${selectedRecord.longitude}#map=17/${selectedRecord.latitude}/${selectedRecord.longitude}`" target="_blank" rel="noopener" class="ml-2 text-primary hover:underline">{{ t('adminAttendance.openMap') }}</a>
            </p>
          </div>
        </div>

        <!-- Photo -->
        <div v-if="selectedRecord.photo_url" class="rounded-lg bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground mb-2">{{ t('adminAttendance.photo') }}</p>
          <img :src="attendancePhotoUrl(selectedRecord.photo_url)" alt="Attendance photo" class="w-full max-h-64 rounded-md object-contain bg-black" @error="e => e.target.style.display='none'" />
        </div>

        <!-- Notes -->
        <div v-if="selectedRecord.notes" class="rounded-lg bg-muted/50 p-3">
          <p class="text-xs text-muted-foreground mb-1">{{ t('adminAttendance.notes') }}</p>
          <p class="text-sm text-foreground whitespace-pre-wrap">{{ selectedRecord.notes }}</p>
        </div>

        <!-- Failed constraints -->
        <div v-if="parsedConstraints.length" class="rounded-lg bg-red-50 border border-red-200 p-3">
          <p class="text-xs font-medium text-red-600 mb-2">Auto-Approval Failed — Constraints</p>
          <ul class="space-y-1">
            <li v-for="c in parsedConstraints" :key="c" class="flex items-center gap-2 text-sm text-red-800">
              <span class="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
              {{ c.replace(/_/g, ' ') }}
            </li>
          </ul>
        </div>

        <!-- Admin notes (reviewed) -->
        <div v-if="selectedRecord.admin_notes" class="rounded-lg bg-primary/5 p-3">
          <p class="text-xs font-medium text-primary mb-1">{{ t('adminAttendance.approvalNotes') }}</p>
          <p class="text-sm text-foreground">{{ selectedRecord.admin_notes }}</p>
          <p v-if="selectedRecord.reviewed_by_name" class="text-xs text-muted-foreground mt-1">{{ t('adminPayslip.by') }} {{ selectedRecord.reviewed_by_name }}</p>
        </div>

        <!-- Approve / Reject actions -->
        <div v-if="selectedRecord.status === 'pending'" class="space-y-3 pt-2 border-t border-border">
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('adminAttendance.approvalNotes') }}</label>
            <textarea v-model="modalAdminNotes" rows="2" :placeholder="t('adminAttendance.addNotes')" class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div v-if="showRejectInput" class="space-y-2">
            <label class="block text-xs font-medium text-destructive mb-1">{{ t('adminAttendance.rejectionReason') }}</label>
            <AppInput v-model="modalRejectReason" :placeholder="t('adminAttendance.rejectionPlaceholder')" />
          </div>
          <div class="flex gap-2">
            <template v-if="!showRejectInput">
              <AppButton variant="success" class="flex-1" :loading="actionLoading" @click="approveSelectedRecord">{{ t('common.approve') }}</AppButton>
              <AppButton variant="destructive" class="flex-1" @click="showRejectInput = true">{{ t('common.reject') }}</AppButton>
            </template>
            <template v-else>
              <AppButton variant="destructive" class="flex-1" :loading="actionLoading" :disabled="!modalRejectReason.trim()" @click="rejectSelectedRecord">{{ t('adminAttendance.confirmReject') }}</AppButton>
              <AppButton variant="outline" @click="showRejectInput = false; modalRejectReason = ''">{{ t('common.cancel') }}</AppButton>
            </template>
          </div>
        </div>
      </div>
    </AppModal>

    <!-- Quick Reject Modal -->
    <AppModal v-model="showQuickRejectModal" :title="t('adminAttendance.rejectRequest')" size="sm">
      <p class="text-sm text-muted-foreground mb-3">{{ t('adminAttendance.rejectReason') }}</p>
      <AppInput v-model="quickRejectReason" :placeholder="t('adminAttendance.rejectionPlaceholder')" @keyup.enter="confirmQuickReject" />
      <template #footer>
        <AppButton variant="outline" :disabled="actionLoading" @click="showQuickRejectModal = false; quickRejectReason = ''; quickRejectRecord = null">{{ t('common.cancel') }}</AppButton>
        <AppButton variant="destructive" :loading="actionLoading" :disabled="!quickRejectReason.trim()" @click="confirmQuickReject">{{ t('common.reject') }}</AppButton>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Download, RefreshCw, UserCheck, UserX, Clock, Users,
  ChevronLeft, ChevronRight, Eye, CheckCircle, XCircle, Search,
} from 'lucide-vue-next';
import dayjs from 'dayjs';
import adminAttendanceService from '@/services/adminAttendanceService';
import adminDepartmentsService from '@/services/adminDepartmentsService';
import { AppPageHeader, AppCard, AppStatCard, AppTable, AppBadge, AppButton, AppInput, AppSelect, AppModal } from '@/components/ui';

const { t } = useI18n();
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/api\/v1\/?$/, '');

const attendancePhotoUrl = (photoUrl) => {
  if (!photoUrl) return '';
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://') || photoUrl.startsWith('/')) return photoUrl;
  return `${API_BASE}/${photoUrl}`;
};

const attendanceRecords = ref([]);
const departments = ref([]);
const loading = ref(false);
const dateRange = ref('today');
const selectedDepartment = ref('');
const selectedStatus = ref('');
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(20);
const totalRecords = ref(0);
const stats = ref({ approved: 0, rejected: 0, pending: 0, total: 0 });

const showModal = ref(false);
const selectedRecord = ref(null);
const modalAdminNotes = ref('');
const modalRejectReason = ref('');
const showRejectInput = ref(false);
const actionLoading = ref(false);

const showQuickRejectModal = ref(false);
const quickRejectRecord = ref(null);
const quickRejectReason = ref('');

const columns = computed(() => [
  { key: 'employee',   label: t('adminAttendance.employee') },
  { key: 'department', label: t('common.department'), mobileHide: true },
  { key: 'type',       label: t('adminAttendance.type') },
  { key: 'time',       label: t('adminAttendance.timestamp') },
  { key: 'status',     label: t('common.status') },
]);

const totalPages = computed(() => Math.max(1, Math.ceil(totalRecords.value / itemsPerPage.value)));

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, start + 4);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

const parsedConstraints = computed(() => {
  if (!selectedRecord.value?.failed_constraints) return [];
  try {
    const v = selectedRecord.value.failed_constraints;
    const parsed = typeof v === 'string' ? JSON.parse(v) : v;
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
});

const statusVariant = (status) => ({
  approved:      'success',
  auto_approved: 'success',
  rejected:      'destructive',
  pending:       'warning',
}[status] ?? 'secondary');

const getDateRange = () => {
  const today = dayjs().format('YYYY-MM-DD');
  if (dateRange.value === 'week')  return { dateFrom: dayjs().startOf('week').format('YYYY-MM-DD'), dateTo: today };
  if (dateRange.value === 'month') return { dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'), dateTo: today };
  return { dateFrom: today, dateTo: today };
};

const loadAttendanceData = async () => {
  try {
    loading.value = true;
    const { dateFrom, dateTo } = getDateRange();
    const params = { dateFrom, dateTo, page: currentPage.value, pageSize: itemsPerPage.value };
    if (selectedDepartment.value) params.department = selectedDepartment.value;
    if (selectedStatus.value) params.status = selectedStatus.value;
    if (searchQuery.value) params.search = searchQuery.value;

    const response = await adminAttendanceService.getAttendanceRecords(params);
    const data = response.data?.data || response.data || {};
    const records = data.records || data.requests || [];
    attendanceRecords.value = records;
    totalRecords.value = data.total || data.pagination?.total || records.length;

    stats.value = {
      approved: records.filter(r => r.status === 'approved' || r.status === 'auto_approved').length,
      rejected: records.filter(r => r.status === 'rejected').length,
      pending:  records.filter(r => r.status === 'pending').length,
      total:    totalRecords.value,
    };
  } catch (error) {
    console.error('Error loading attendance data:', error);
  } finally {
    loading.value = false;
  }
};

const loadDepartments = async () => {
  try {
    const data = await adminDepartmentsService.getDepartments();
    const list = data?.data?.departments || data?.data || data || [];
    departments.value = Array.isArray(list) ? list : [];
  } catch (error) {
    console.error('Error loading departments:', error);
  }
};

const initials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

const formatTime = (ts) => {
  if (!ts) return '-';
  const d = dayjs(ts);
  return d.isValid() ? d.format('HH:mm') : '-';
};

const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = dayjs(ts);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '-';
};

const getStatusText = (status) => ({
  approved:      t('common.approved'),
  auto_approved: t('adminAttendance.autoApproved'),
  rejected:      t('common.rejected'),
  pending:       t('common.pending'),
}[status] ?? status ?? t('adminAttendance.unknown'));

const viewDetails = (record) => {
  selectedRecord.value = record;
  modalAdminNotes.value = '';
  modalRejectReason.value = '';
  showRejectInput.value = false;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedRecord.value = null;
};

const approveSelectedRecord = async () => {
  if (!selectedRecord.value) return;
  actionLoading.value = true;
  try {
    await adminAttendanceService.approveRequest(selectedRecord.value.id, modalAdminNotes.value);
    selectedRecord.value.status = 'approved';
    const rec = attendanceRecords.value.find(r => r.id === selectedRecord.value.id);
    if (rec) rec.status = 'approved';
    closeModal();
    await loadAttendanceData();
  } catch (err) {
    alert('Failed to approve: ' + (err.response?.data?.message || err.message));
  } finally {
    actionLoading.value = false;
  }
};

const rejectSelectedRecord = async () => {
  if (!selectedRecord.value || !modalRejectReason.value.trim()) return;
  actionLoading.value = true;
  try {
    await adminAttendanceService.rejectRequest(selectedRecord.value.id, modalRejectReason.value, modalAdminNotes.value);
    selectedRecord.value.status = 'rejected';
    const rec = attendanceRecords.value.find(r => r.id === selectedRecord.value.id);
    if (rec) rec.status = 'rejected';
    closeModal();
    await loadAttendanceData();
  } catch (err) {
    alert('Failed to reject: ' + (err.response?.data?.message || err.message));
  } finally {
    actionLoading.value = false;
  }
};

const quickApprove = async (record) => {
  if (!confirm(`Approve ${record.type === 'clock_in' ? 'clock-in' : 'clock-out'} request for ${record.employee?.name || record.userName}?`)) return;
  actionLoading.value = true;
  try {
    await adminAttendanceService.approveRequest(record.id);
    record.status = 'approved';
    await loadAttendanceData();
  } catch (err) {
    alert('Failed to approve: ' + (err.response?.data?.message || err.message));
  } finally {
    actionLoading.value = false;
  }
};

const quickReject = (record) => {
  quickRejectRecord.value = record;
  quickRejectReason.value = '';
  showQuickRejectModal.value = true;
};

const confirmQuickReject = async () => {
  if (!quickRejectRecord.value || !quickRejectReason.value.trim()) return;
  actionLoading.value = true;
  try {
    await adminAttendanceService.rejectRequest(quickRejectRecord.value.id, quickRejectReason.value);
    quickRejectRecord.value.status = 'rejected';
    showQuickRejectModal.value = false;
    quickRejectRecord.value = null;
    quickRejectReason.value = '';
    await loadAttendanceData();
  } catch (err) {
    alert('Failed to reject: ' + (err.response?.data?.message || err.message));
  } finally {
    actionLoading.value = false;
  }
};

const refreshAttendance = () => { currentPage.value = 1; loadAttendanceData(); };

const exportAttendance = () => {
  if (!attendanceRecords.value.length) { alert('No data to export'); return; }
  const headers = ['ID', 'Employee', 'Code', 'Department', 'Type', 'Time', 'Status', 'Address'];
  const rows = attendanceRecords.value.map(r => [
    r.id, r.employee?.name || r.userName || '', r.employee?.employee_code || r.employee_code || '',
    r.department || '', r.type || '',
    r.requested_timestamp ? dayjs(r.requested_timestamp).format('YYYY-MM-DD HH:mm') : '',
    r.status || '', r.address || ''
  ]);
  const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `attendance_${dayjs().format('YYYY-MM-DD')}.csv`; a.click();
  URL.revokeObjectURL(url);
};

const previousPage = () => { if (currentPage.value > 1) currentPage.value--; };
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };
const goToPage = (page) => { currentPage.value = page; };

watch([dateRange, selectedDepartment, selectedStatus, searchQuery], () => { currentPage.value = 1; loadAttendanceData(); });
watch(currentPage, loadAttendanceData);

onMounted(() => { loadAttendanceData(); loadDepartments(); });
</script>
